// ═══════════════════════════════════════════════════════════════
// Offline Indicator — shows banner when offline, sync status
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { Text, Badge } from '@mantine/core';
import { IconWifiOff, IconRefresh } from '@tabler/icons-react';

export default function OfflineIndicator() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queueSize] = useState(0);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => {
      setIsOffline(false);
      setJustReconnected(true);
      setTimeout(() => setJustReconnected(false), 4000);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (!isOffline && !justReconnected) return null;

  if (justReconnected) {
    return (
      <div style={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, padding: '8px 20px', borderRadius: 8,
        background: '#16a34a', color: 'white',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex', alignItems: 'center', gap: 8,
        animation: 'fadeIn 0.3s ease',
      }}>
        <IconRefresh size={16} />
        <Text size="sm" fw={500}>Back online — syncing queued changes...</Text>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, padding: '8px 20px', borderRadius: 8,
      background: '#dc2626', color: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <IconWifiOff size={16} />
      <Text size="sm" fw={500}>You are offline</Text>
      {queueSize > 0 && (
        <Badge size="sm" color="yellow" variant="filled">
          {queueSize} queued
        </Badge>
      )}
    </div>
  );
}

export { OfflineIndicator };
